import { useContext } from 'react'
import { StaticDataImporterContext } from '../context/StaticDataImporterContext'
import { TemporaryDataType } from '../temporary-data-table/useTemporaryDataState'

export const useMoveEntityRowToAnotherEntityRowParent = (): {
    moveRowToAnotherParent: (
        record: EntityDto,
        selectedParent: number,
        handleEditSave: (record: EntityDto) => void
    ) => void
} => {
    const { checkedRows, setCheckedRows, data } = useContext(StaticDataImporterContext)

    const moveRowToAnotherParent = (
        record: EntityDto,
        selectedParent: number,
        handleEditSave: (record: EntityDto) => void
    ): void => {
        let recordEntityName: keyof TemporaryDataType
        let newParent: EntityDto | undefined

        // Finding the entity name of the record being moved and the new parent entity based on the selected parent rowId
        Object.entries(data).forEach(([entityName, entities]) => {
            // Checking if the current entity contains the record being moved
            if (entities?.find(e => String(e.rowId) === String(record.rowId))) {
                recordEntityName = entityName as keyof TemporaryDataType
            }

            // Checking if the current entity contains the selected parent rowId to find the new parent entity
            if (entities?.find(e => String(e.rowId) === String(selectedParent))) {
                newParent = entities.find(e => String(e.rowId) === String(selectedParent))
            }
        })

        // If the new parent is not found, we cannot proceed with the move operation
        if (!newParent || !recordEntityName) {
            return
        }

        // Storing the old record before updating it, so we can compare and update the checkedRows state accordingly
        const oldRecord = { ...record }

        // Creating a new record with the updated parent information
        const newRecord = {
            ...record,
            parent: newParent.nodeId,
            frontParentUuid: newParent.frontUuid
        }

        // Updating the record with the new parent information
        handleEditSave({
            ...record,
            parent: newParent.nodeId,
            frontParentUuid: newParent.frontUuid
        })

        // If the moved record is checked, we need to update the checkedRows state to reflect the new parent-child relationship
        handleChackedRowsWhenMove(oldRecord, newRecord, recordEntityName, newParent, data, checkedRows, setCheckedRows)
    }

    return {
        moveRowToAnotherParent
    }
}

export const handleChackedRowsWhenMove = (
    oldRecord: EntityDto,
    newRecord: EntityDto,
    recordEntityName: keyof TemporaryDataType,
    newParent: EntityDto,
    data: TemporaryDataType,
    checkedRows: Record<string, Entity>,
    setCheckedRows: React.Dispatch<React.SetStateAction<Record<string, Entity>>>
): void => {
    const newCheckedRows = { ...(checkedRows ?? {}) }
    const recordKey = `${recordEntityName}=>${oldRecord.parent}`
    const isCheckedRowsInitiallyEmpty = Object.keys(newCheckedRows).length === 0

    if (!isCheckedRowsInitiallyEmpty) {
        // Removing the record from the old parent key
        if (newCheckedRows[recordKey]) {
            newCheckedRows[recordKey] = newCheckedRows[recordKey].filter(
                item => String(item.rowId) !== String(oldRecord.rowId)
            ) as Entity
            if (!newCheckedRows[recordKey].length) {
                delete newCheckedRows[recordKey]
            }
        }

        // Adding the record to the new parent key
        newCheckedRows[`${recordEntityName}=>${newParent.nodeId}`] = [
            ...(newCheckedRows[`${recordEntityName}=>${newParent.nodeId}`] as Entity),
            {
                ...oldRecord,
                ...newRecord
            }
        ] as Entity

        setCheckedRows(newCheckedRows)
    }
}
